import './index.css'

import cn from 'clsx'
import get from 'lodash/get'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import {
  NavigateFunction,
  Outlet,
  useNavigate,
  useParams,
} from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { DEFAULT_LINE_HEIGHT } from '@disco/constants/content-editor'
import { EMAIL_CAMPAIGN_BUILDER } from '@disco/constants/FeatureTypes'
import { WATERMARKED } from '@disco/constants/WatermarkStatusesTypes'
import { getFontFamily, getFontSize } from '@disco/content-editor/editor-utils'
import {
  campaignBeginSending,
  cancelScheduledCampaign,
} from '@disco/state-management/actions/campaign-details-actions'
import { makeIsFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getCampaign,
  getCampaignEditorState,
  getValidCampaignSteps,
  isCampaignSendAllowed,
} from '@disco/state-management/selectors/campaigns'
import {
  getContactsFilteredByCampaignRules,
  isContactsLoaded,
} from '@disco/state-management/selectors/contacts-all'
import {
  getCampaignSenderWithDefaults,
  getTemplateData,
} from '@disco/utils/emailCampaigns'
import { plural } from '@disco/utils/string-utils'

import type { EmailCampaign } from 'types/campaign'
import type { CampaignSection } from 'types/content-editor'
import type { Contact } from 'types/list'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import type { PopoverItemType } from 'types/ui'
import type { User } from 'types/user'

import DocumentHeadTags from 'components/document-head-tags'
import WatermarkingNotFinishedModal from 'components/modals/watermarking-not-finished'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ContactUsLink from 'components/ui/contact-us-link'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Flex from 'components/ui/flex'
import ModalDialog from 'components/ui/modal-dialog'
import PageContainer from 'components/ui/page-container'

import BrowserFrame from 'pages/campaigns/browser-frame'
import CampaignHeader from 'pages/campaigns/campaign-header'
import CampaignNav, { CONFIRM_AND_SEND_TAB } from 'pages/campaigns/campaign-nav'
import ContentEditor from 'pages/campaigns/editor/content-editor'
import LiveCountdown from 'pages/campaigns/preview/live-countdown'

import type { RootState } from 'store/configureStore'

function mapStateToProps(
  state: RootState,
  ownProps: {
    params: {
      campaignId: string
    }
  },
) {
  const emailCampaign = getCampaign(state, {
    campaignId: parseInt(ownProps.params.campaignId),
  })
  const isFeatureEnabled = makeIsFeatureEnabled()
  const campaignEditor = getCampaignEditorState(state)
  const isFeatureEnabled2 = makeIsFeatureEnabled()
  return {
    user: state.user,
    emailGroups: state.emailGroups,
    campaign: emailCampaign,
    filteredContacts:
      getContactsFilteredByCampaignRules(state).filteredContacts,
    contactsLoaded: isContactsLoaded(state),
    campaignTemplate: campaignEditor.sections,
    isFeatureEnabled: isFeatureEnabled(state, {
      featureName: EMAIL_CAMPAIGN_BUILDER,
    }),
    validCampaignSteps: getValidCampaignSteps(
      state,
      ownProps.params.campaignId,
    ),
    campaignSendAllowed: isCampaignSendAllowed(
      state,
      ownProps.params.campaignId,
    ),
  }
}

const mapDispatchToProps = {
  campaignBeginSending,
  cancelScheduledCampaign,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

interface AdditionalProps {
  navigate: NavigateFunction
  params: {
    campaignId: string
  }
}

type Props = PropsFromRedux & AdditionalProps

class CampaignPreviewPage extends React.Component<
  Props,
  {
    sendConfirmationModal: boolean
    watermarkConfirmationModal: boolean
    watermarkingNotFinished: Playlist[] | null | undefined
  }
> {
  state = {
    sendConfirmationModal: false,
    watermarkConfirmationModal: false,
    watermarkingNotFinished: null,
  }
  _checkWatermarkingNotFinished = () => {
    const playlists = this.props.campaign?.playlists
    let updated = []

    if (playlists) {
      updated = Object.keys(playlists)
        .map(key => playlists[Number(key)])
        .filter(
          ({ watermark, watermarking_status }) =>
            watermark && watermarking_status !== WATERMARKED,
        )
    }

    this.setState({
      watermarkConfirmationModal: Boolean(updated.length),
      sendConfirmationModal: !Boolean(updated.length),
      watermarkingNotFinished: updated.length ? updated : null,
    })
  }
  _handleEditRecipients = () => {
    const { campaignId } = this.props.params
    if (!campaignId) return
    this.props.navigate(`/campaigns/${campaignId}/recipients`)
  }
  _handleEditDetails = () => {
    const { campaignId } = this.props.params
    if (!campaignId) return
    this.props.navigate(`/campaigns/${campaignId}/details`)
  }
  _handleEditContents = () => {
    const { campaignId } = this.props.params
    if (!campaignId) return
    this.props.navigate(`/campaigns/${campaignId}/editor`)
  }
  _handleCancelSending = () => {
    this.setState({
      sendConfirmationModal: false,
      watermarkConfirmationModal: false,
    })
  }
  _handleStartSending = () => {
    const { campaign } = this.props
    if (!campaign) return null
    mixpanel.sendPureEvent('Email Creator - Send email')
    this.setState({
      sendConfirmationModal: false,
    })
    this.props.campaignBeginSending(campaign.id)
  }
  _handleCancelSchedule = () => {
    this.props.cancelScheduledCampaign(Number(this.props.params.campaignId))
  }
  _handleRescheduleCampaign = () => {
    this.props.navigate(
      `/campaigns/${this.props.params.campaignId}/confirm/schedule-later`,
    )
  }
  _onActionClick = value => {
    if (value.includes('reschedule_campaign')) {
      this._handleRescheduleCampaign()
    }

    if (value.includes('send_now')) {
      this._checkWatermarkingNotFinished()
    }

    if (value.includes('cancel_scheduled_campaign')) {
      mixpanel.sendPureEvent('Email Creator - Cancel email scheduling', {
        Context: 'Email',
      })
      this.props.cancelScheduledCampaign(Number(this.props.params.campaignId))
    }
  }

  render() {
    const {
      filteredContacts,
      campaign,
      isFeatureEnabled,
      contactsLoaded,
      campaignSendAllowed,
    } = this.props
    const {
      validSubject,
      validRecipients,
      validAddress,
      validSender,
      valid,
      sendAllowed,
    } = this.props.validCampaignSteps
    const { watermarkingNotFinished, watermarkConfirmationModal } = this.state
    if (!campaign) return null
    const template = this.props.campaignTemplate
    const template_data = getTemplateData(campaign)
    const boldNumberOfRecipients = (
      <span>
        <b>{filteredContacts.length}</b>{' '}
        {filteredContacts.length === 1 ? (
          <span>recipient</span>
        ) : (
          <span>recipients</span>
        )}
      </span>
    )
    const numberOfRecipients = !contactsLoaded ? (
      <span>Loading...</span>
    ) : (
      <span>{plural(filteredContacts.length, 'recipient', 'recipients')}</span>
    )
    const { sender_name, sender_email } = getCampaignSenderWithDefaults(
      campaign,
      this.props.user,
      this.props.emailGroups.emailGroups,
    )
    let sendButtonTooltip

    if (!contactsLoaded) {
      sendButtonTooltip =
        'Contacts are still loading. You’ll be able to send the email when they’ve loaded.'
    }

    if (!isFeatureEnabled && valid && sendAllowed) {
      sendButtonTooltip = (
        <>
          <ContactUsLink>Upgrade</ContactUsLink> your plan to start sending
          campaigns.
        </>
      )
    }

    const rescheduleSendOptions: PopoverItemType[] = [
      {
        value: 'reschedule_campaign',
        label: 'Reschedule',
        type: 'link',
      },
      {
        value: 'send_now',
        label: 'Send now',
        type: 'link',
      },
      {
        value: 'cancel_scheduled_campaign',
        label: 'Cancel schedule',
        type: 'link',
        destructive: true,
      },
    ]
    const navbar = (
      <CampaignHeader
        campaignId={this.props.params.campaignId}
        nextButton={
          <ButtonContainer>
            {campaign.status !== 'scheduled' && (
              <Button
                icon="email-scheduled"
                caption="Schedule later"
                disabled={!campaignSendAllowed}
                tooltip={sendButtonTooltip}
                link={`/campaigns/${this.props.params.campaignId}/confirm/schedule-later`}
              />
            )}
            {campaign.status === 'scheduled' && (
              <Flex>
                <LiveCountdown campaignId={this.props.params.campaignId} />
                <EllipsisMenu
                  behavior="button-dropdown"
                  options={rescheduleSendOptions}
                  disabled={!campaignSendAllowed}
                  tooltip={
                    !campaignSendAllowed
                      ? 'Contacts are still loading. You’ll be able to reschedule the email when they’ve loaded.'
                      : undefined
                  }
                  onOptionClick={this._onActionClick}
                />
              </Flex>
            )}
            {campaign.status !== 'scheduled' && (
              <Button
                appearance="primary"
                caption="Send email"
                disabled={!campaignSendAllowed}
                progressCaption="Sending"
                progressIndicator={campaign.sending}
                onClick={this._checkWatermarkingNotFinished}
                tooltip={sendButtonTooltip}
              />
            )}
          </ButtonContainer>
        }
      />
    )
    const campaignStyle = {
      backgroundColor: get(
        campaign.template_data,
        'campaignEditor.generalBackground',
        '#fff',
      ),
      padding: '50px 0',
    }
    return (
      <div className="Page_Content">
        <DocumentHeadTags
          title="Preview | DISCO"
          link={{
            href: 'https://fonts.googleapis.com/css2?family=Roboto&display=swap',
            rel: 'stylesheet',
          }}
        />
        {navbar}
        {this.state.sendConfirmationModal && (
          <ModalDialog
            title="Send campaign"
            primaryAction={{
              caption: 'Send',
              onClick: this._handleStartSending,
            }}
            status="warning"
            onCancel={this._handleCancelSending}
          >
            You’re about to send this email to {boldNumberOfRecipients}. Are you
            sure you’re ready to send? Sending can’t be undone.
          </ModalDialog>
        )}
        {watermarkConfirmationModal && watermarkingNotFinished && (
          <WatermarkingNotFinishedModal
            playlistName={watermarkingNotFinished[0]?.name}
            onConfirm={this._handleStartSending}
            onCancel={this._handleCancelSending}
          />
        )}
        <PageContainer>
          <CampaignNav
            campaignId={this.props.params.campaignId}
            activeStep={CONFIRM_AND_SEND_TAB}
          />
          <PageContainer.Section>
            <div className="CampaignPreview_BrowserFrame">
              <BrowserFrame>
                <div className="CampaignPreview_FieldRow">
                  <div className="CampaignPreview_FieldName">To:</div>
                  <div className="CampaignPreview_FieldValue">
                    {numberOfRecipients}
                  </div>
                  <div
                    className={cn('CampaignPreview_Btn', {
                      CampaignPreview_Invalid: !validRecipients,
                    })}
                  >
                    <Button
                      caption="Edit recipients"
                      icon="pen"
                      onClick={this._handleEditRecipients}
                    />
                  </div>
                </div>
                <div className="CampaignPreview_FieldRow">
                  <div
                    className={cn('CampaignPreview_Btn', {
                      CampaignPreview_Invalid: !validSubject,
                    })}
                  >
                    <Button
                      caption={validSubject ? 'Edit subject' : 'Add subject'}
                      icon="pen"
                      onClick={this._handleEditDetails}
                    />
                  </div>
                  <div className="CampaignPreview_FieldName">Subject:</div>
                  <div
                    className="CampaignPreview_FieldValue"
                    title={template_data.subject}
                  >
                    {template_data.subject}
                  </div>
                </div>
                <div className="CampaignPreview_FieldRow">
                  <div
                    className={cn('CampaignPreview_Btn', {
                      CampaignPreview_Invalid: !validSender,
                    })}
                  >
                    <Button
                      caption="Edit sender"
                      icon="pen"
                      onClick={this._handleEditDetails}
                    />
                  </div>
                  <div className="CampaignPreview_FieldName">From:</div>
                  <div className="CampaignPreview_FieldValue">
                    {sender_name} {validSender ? <span>|</span> : null}{' '}
                    {sender_email}
                  </div>
                </div>
                <div className="CampaignPreview_Preview" style={campaignStyle}>
                  <div className="CampaignPreview_Btn">
                    <Button
                      caption="Edit contents"
                      icon="pen"
                      onClick={this._handleEditContents}
                    />
                  </div>
                  <div
                    className={cn('CampaignPreview_Btn bottom', {
                      CampaignPreview_Invalid: !validAddress,
                    })}
                  >
                    <Button
                      caption={validAddress ? 'Edit address' : 'Add address'}
                      icon="pen"
                      onClick={this._handleEditDetails}
                    />
                  </div>
                  <ContentEditor
                    className="CampaignPreview_Content"
                    readOnly
                    campaign={{
                      id: campaign.id,
                      fontFamily: campaign.template_data
                        ? getFontFamily(
                            null,
                            campaign.template_data.campaignEditor,
                          )
                        : undefined,
                      fontSize: campaign.template_data
                        ? `${getFontSize(
                            null,
                            campaign.template_data.campaignEditor,
                          )}px`
                        : undefined,
                      lineHeight: get(
                        campaign.template_data,
                        'campaignEditor.lineHeight',
                        DEFAULT_LINE_HEIGHT,
                      ),
                      horizontalPadding: get(
                        campaign.template_data,
                        'campaignEditor.horizontalPadding',
                        '25',
                      ),
                      verticalPadding: get(
                        campaign.template_data,
                        'campaignEditor.verticalPadding',
                        '12',
                      ),
                      sections: template,
                      generalBackground: get(
                        campaign.template_data,
                        'campaignEditor.generalBackground',
                        '#fff',
                      ),
                    }}
                    user={this.props.user as unknown as User}
                  />
                </div>
              </BrowserFrame>
            </div>
          </PageContainer.Section>
        </PageContainer>
        <Outlet />
      </div>
    )
  }
}

const ConnectedCampaignPreviewPage = connector(CampaignPreviewPage)

function CampaignPreviewPageWithRouter() {
  const navigate = useNavigate()
  const params = useParams<{ campaignId: string }>()
  return (
    <ConnectedCampaignPreviewPage
      navigate={navigate}
      params={params as { campaignId: string }}
    />
  )
}

export default CampaignPreviewPageWithRouter
